import { ApolloClient } from "@apollo/client";
import "@homebound/activesupport";
import { AmplifyConfig, createApolloClientWithAuth } from "@homebound/auth-components";
import merge from "lodash/merge";
import { possibleTypes } from "src/generated/graphql-types";
import { typePolicies } from "src/typePolicies";

type Stage = "local" | "dev" | "qa" | "prod";

export function getStage(windowLocation = window.location): Stage {
  // Guess stage based on the current window.location.hostname
  const hostname = windowLocation.hostname;
  if (hostname.includes("dev-homebound")) {
    return "dev";
  } else if (hostname.includes("qa-homebound")) {
    return "qa";
  } else if (hostname.includes("homebound")) {
    return "prod";
  } else {
    return "local";
  }
}

export function isContentPreview(windowLocation = window.location): boolean {
  return /.*\.propco\.(dev|qa)-homebound\.com/.test(windowLocation.hostname);
}

export function getGraphQLBaseUrl(stage: Stage = getStage()): string {
  const stageToGraphQL: Record<Stage, string> = {
    local: "VITE_GRAPHQL_SERVICE" in global && VITE_GRAPHQL_SERVICE ? VITE_GRAPHQL_SERVICE : "http://localhost:4004",
    dev: "https://checkout-graphql.dev-homebound.com",
    qa: "https://checkout-graphql.qa-homebound.com",
    prod: "https://checkout-graphql.homebound.com",
  };
  return stageToGraphQL[stage];
}

export function getBlueprintBaseUrl(stage: Stage = getStage()): string {
  const stageToGraphQL: Record<Stage, string> = {
    local: "VITE_GRAPHQL_SERVICE" in global && VITE_GRAPHQL_SERVICE ? VITE_GRAPHQL_SERVICE : "http://localhost:3000",
    dev: "https://blueprint.dev-homebound.com",
    qa: "https://blueprint.qa-homebound.com",
    prod: "https://blueprint.homebound.com",
  };
  return stageToGraphQL[stage];
}

export function getSalesforceBaseUrl(stage: Stage = getStage()): string {
  // Send to Sandbox unless we're in Prod
  const stageToGraphQL: Record<Stage, string> = {
    local:
      "VITE_GRAPHQL_SERVICE" in global && VITE_GRAPHQL_SERVICE
        ? VITE_GRAPHQL_SERVICE
        : "https://homebound--partialsb.sandbox.lightning.force.com/lightning/r",
    dev: "https://homebound--partialsb.sandbox.lightning.force.com/lightning/r",
    qa: "https://homebound--partialsb.sandbox.lightning.force.com/lightning/r",
    prod: "https://homebound.lightning.force.com/lightning/r",
  };
  return stageToGraphQL[stage];
}

export function getUnderwritingBaseUrl(stage: Stage = getStage()): string {
  const stageToGraphQL: Record<Stage, string> = {
    local: "VITE_GRAPHQL_SERVICE" in global && VITE_GRAPHQL_SERVICE ? VITE_GRAPHQL_SERVICE : "http://localhost:1337",
    dev: "https://underwriting.dev-homebound.com",
    qa: "https://underwriting.qa-homebound.com",
    prod: "https://underwriting.homebound.com",
  };
  return stageToGraphQL[stage];
}

export function createApolloClient(stage: Stage): ApolloClient<unknown> {
  const baseUrl = getGraphQLBaseUrl(stage);

  const client = createApolloClientWithAuth(`${baseUrl}/graphql`, {
    serviceName: "propco-frontend",
    serviceVersion: VITE_GIT_COMMIT ?? "local",
    retryLinkOptions: {
      attempts: {
        retryIf(error: any) {
          if (error instanceof TypeError) {
            console.error("Retrying because fetched rejected: " + error.message);
          }
          // For a failed mutation, error will be a ServerError (error.name === 'ServerName'), don't retry
          // For a rejected fetch (likely transient connectivity issue), error will be a TypeError, so retry
          return error instanceof TypeError;
        },
      },
    },
    apolloClientOptions: {
      connectToDevTools: stage !== "prod",
      assumeImmutableResults: true,
      defaultOptions: {
        // Prefer consistency on CRUD tables/forms. Use watchQuery b/c that's what React's hooks use.
        // For nextFetchPolicy, see https://github.com/apollographql/apollo-client/pull/6712
        watchQuery: { fetchPolicy: "network-only", nextFetchPolicy: "cache-first" },
        query: { fetchPolicy: "network-only" },
      },
    },
    inMemoryCacheOptions: {
      // `as any` because our possibleTypes is generated with `as const` which makes readonly arrays,
      // but Apollo accepts mutable arrays.
      possibleTypes: possibleTypes as any,
      typePolicies,
    },
  });

  return client;
}

// Include `aws.cognito.signin.user.admin` so that we can access user attributes like their profile picture:
// https://stackoverflow.com/a/53151260/355031
const scope = ["phone", "email", "openid", "aws.cognito.signin.user.admin"];

export function amplifyConfig(stage: Stage): AmplifyConfig {
  function stageConfig() {
    if (stage === "prod") {
      return {
        Auth: {
          userPoolId: "us-west-2_Sud41U2uX",
          userPoolWebClientId: "6jbhcfnuji5mor48dg2hg37hvg",
          oauth: { domain: "homebound-internal-prod.auth.us-west-2.amazoncognito.com", scope },
          // Use cookie storage so that non-GraphQL calls (i.e. image downloads) still get the auth token
          cookieStorage: { domain: "propco.homebound.com" },
        },
      };
    }

    return {
      Auth: {
        userPoolId: "us-west-2_u4KsZS7DF",
        userPoolWebClientId: "47jne7hh7uao4il33eq1kftr5c",
        oauth: { domain: "homebound-internal-dev.auth.us-west-2.amazoncognito.com", scope },
        cookieStorage: {
          // Use .<stage>-homebound.com so that our cookies get sent to graphql.<stage>-homebound.com, which
          // knows to look for the cookie as well as the usual Authentication header, so that the
          // GraphQL playground gets auth "for free".
          //
          // This domain pattern is too broad for production, i.e. it would leak our auth cookies onto
          // domains like `somechatvendor.homebound.com`, but it's fine for dev creds.
          //
          // We should also not conflict/overlap with other apps like `app.<stage>-homebound.com` because
          // Cognito puts the application-specific user pool id into the cookie names.
          domain: stage === "local" ? "localhost" : `.${stage}-homebound.com`,
          // Don't use secure so that the cookie works on the http://localhost:4004/graphql GraphQL playground.
          secure: false,
        },
      },
    };
  }

  const { origin } = window.location;

  return merge({}, stageConfig(), {
    Auth: {
      region: "us-west-2",
      oauth: {
        scope: ["phone", "email", "openid"],
        redirectSignIn: origin + "/auth/gcallback",
        redirectSignOut: origin + "/",
        responseType: "code" as const,
      },
    },
  });
}

export const lightGalleryKey = "3AA3D7F6-B4F64FA5-886A68F8-B0DF2E97";
